import type { ChatModeValue, NewsJobMode, NewsNotificationType } from "./api";

export type ReceiptJobNotification = {
  kind: "receipt";
  jobId: string;
  status: "completed" | "failed";
  processedAt?: string;
  extractedSummary?: string;
  failedStage?: string;
  failedError?: string;
};

export type NewsJobNotification = {
  kind: "news";
  jobId: string;
  status: "completed" | "failed";
  processedAt?: string;
  keyword?: string;
  notificationType?: NewsNotificationType;
  mode?: NewsJobMode;
  failedStage?: string;
  failedError?: string;
  callbackMessage?: string;
  summary?: string;
};

export type LevelUpNotification = {
  kind: "level_up";
  characterId: string;
  rankName: string;
};

export type ModeUnlockNotification = {
  kind: "mode_unlock";
  characterId: string;
  mode: ChatModeValue;
  modeName?: string;
};

export type PendingJobNotification =
  | ReceiptJobNotification
  | NewsJobNotification
  | LevelUpNotification
  | ModeUnlockNotification;
export type PendingJobNotificationKind = PendingJobNotification["kind"];
type PendingJobStatus = ReceiptJobNotification["status"] | NewsJobNotification["status"];

const JOB_NOTIFICATION_STORAGE_KEY = "wizmate.job-notifications.v1";
export const JOB_NOTIFICATION_EVENT = "wizmate:job-notifications";

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function hasWindow() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function isNotificationStatus(
  value: unknown
): value is PendingJobStatus {
  return value === "completed" || value === "failed";
}

function isNotificationKind(
  value: unknown
): value is PendingJobNotificationKind {
  return value === "receipt" || value === "news" || value === "level_up" || value === "mode_unlock";
}

function normalizeChatModeValue(value: unknown): ChatModeValue | null {
  if (value === 0 || value === 1) return value;
  if (value === "0" || value === "1") return Number(value) as ChatModeValue;
  return null;
}

function parsePendingJobNotification(value: unknown): PendingJobNotification | null {
  if (!value || typeof value !== "object") return null;

  const candidate = value as Partial<PendingJobNotification>;
  const kind = candidate.kind;

  if (!isNotificationKind(kind)) {
    return null;
  }

  if (kind === "receipt") {
    const jobId = normalize((candidate as Partial<ReceiptJobNotification>).jobId);
    const status = candidate.status;
    if (!jobId || !isNotificationStatus(status)) {
      return null;
    }
    return {
      kind,
      jobId,
      status,
      processedAt: normalize(candidate.processedAt),
      extractedSummary: normalize((candidate as Partial<ReceiptJobNotification>).extractedSummary),
      failedStage: normalize(candidate.failedStage),
      failedError: normalize(candidate.failedError),
    };
  }

  if (kind === "news") {
    const newsCandidate = candidate as Partial<NewsJobNotification>;
    const jobId = normalize(newsCandidate.jobId);
    const status = candidate.status;
    if (!jobId || !isNotificationStatus(status)) {
      return null;
    }
    const notificationType =
      newsCandidate.notificationType === "line" ||
      newsCandidate.notificationType === "google_chat" ||
      newsCandidate.notificationType === "email"
        ? newsCandidate.notificationType
        : undefined;
    const mode = newsCandidate.mode === "news" || newsCandidate.mode === "report"
      ? newsCandidate.mode
      : undefined;

    return {
      kind,
      jobId,
      status,
      processedAt: normalize(candidate.processedAt),
      keyword: normalize(newsCandidate.keyword),
      notificationType,
      mode,
      failedStage: normalize(candidate.failedStage),
      failedError: normalize(candidate.failedError),
    };
  }

  if (kind === "level_up") {
    const characterId = normalize((candidate as Partial<LevelUpNotification>).characterId);
    const rankName = normalize((candidate as Partial<LevelUpNotification>).rankName);
    if (!characterId || !rankName) {
      return null;
    }
    return {
      kind,
      characterId,
      rankName,
    };
  }

  const characterId = normalize((candidate as Partial<ModeUnlockNotification>).characterId);
  const mode = normalizeChatModeValue((candidate as Partial<ModeUnlockNotification>).mode);
  const modeName = normalize((candidate as Partial<ModeUnlockNotification>).modeName);
  if (!characterId || mode === null) {
    return null;
  }
  return {
    kind,
    characterId,
    mode,
    modeName,
  };
}

function dispatchJobNotificationEvent(notifications: PendingJobNotification[]) {
  if (!hasWindow()) return;

  window.dispatchEvent(
    new CustomEvent<PendingJobNotification[]>(JOB_NOTIFICATION_EVENT, {
      detail: notifications,
    })
  );
}

function writePendingJobNotifications(notifications: PendingJobNotification[]) {
  if (!hasWindow()) return;

  if (!notifications.length) {
    window.localStorage.removeItem(JOB_NOTIFICATION_STORAGE_KEY);
    dispatchJobNotificationEvent([]);
    return;
  }

  window.localStorage.setItem(JOB_NOTIFICATION_STORAGE_KEY, JSON.stringify(notifications));
  dispatchJobNotificationEvent(notifications);
}

export function getPendingJobNotificationKey(notification: PendingJobNotification) {
  if (notification.kind === "level_up") {
    return [notification.kind, notification.characterId, notification.rankName].join(":");
  }

  if (notification.kind === "mode_unlock") {
    return [notification.kind, notification.characterId, notification.mode].join(":");
  }

  return [
    notification.kind,
    notification.jobId,
    notification.status,
    notification.processedAt ?? "",
    notification.failedStage ?? "",
    notification.failedError ?? "",
    notification.kind === "receipt" ? notification.extractedSummary ?? "" : notification.keyword ?? "",
    notification.kind === "news" ? notification.notificationType ?? "" : "",
  ].join(":");
}

export function readPendingJobNotifications(kind?: PendingJobNotificationKind) {
  if (!hasWindow()) return [] as PendingJobNotification[];

  const raw = window.localStorage.getItem(JOB_NOTIFICATION_STORAGE_KEY);
  if (!raw) return [] as PendingJobNotification[];

  try {
    const parsed = JSON.parse(raw);
    const values = Array.isArray(parsed) ? parsed : [parsed];
    const seen = new Set<string>();

    return values
      .map((value) => parsePendingJobNotification(value))
      .filter((value): value is PendingJobNotification => value !== null)
      .filter((notification) => (kind ? notification.kind === kind : true))
      .filter((notification) => {
        const key = getPendingJobNotificationKey(notification);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  } catch {
    return [] as PendingJobNotification[];
  }
}

export function readPendingJobNotification(kind?: PendingJobNotificationKind) {
  return readPendingJobNotifications(kind)[0] ?? null;
}

export function writePendingJobNotification(notification: PendingJobNotification) {
  const normalized = parsePendingJobNotification(notification);
  if (!normalized) return;

  const nextKey = getPendingJobNotificationKey(normalized);
  const nextNotifications = [
    normalized,
    ...readPendingJobNotifications().filter(
      (item) => getPendingJobNotificationKey(item) !== nextKey
    ),
  ];

  writePendingJobNotifications(nextNotifications);
}

export function removePendingJobNotification(notification: PendingJobNotification) {
  const nextKey = getPendingJobNotificationKey(notification);
  const nextNotifications = readPendingJobNotifications().filter(
    (item) => getPendingJobNotificationKey(item) !== nextKey
  );

  writePendingJobNotifications(nextNotifications);
}

export function clearPendingJobNotifications(kind?: PendingJobNotificationKind) {
  if (!kind) {
    writePendingJobNotifications([]);
    return;
  }

  const nextNotifications = readPendingJobNotifications().filter(
    (item) => item.kind !== kind
  );
  writePendingJobNotifications(nextNotifications);
}

export function consumePendingJobNotification(kind?: PendingJobNotificationKind) {
  const notifications = readPendingJobNotifications();
  const index = notifications.findIndex((notification) => (kind ? notification.kind === kind : true));

  if (index < 0) {
    return null;
  }

  const [notification] = notifications.splice(index, 1);
  writePendingJobNotifications(notifications);
  return notification;
}
